/**
 * This file contains utility functions for handling invoice and member details.
 */
import { useDispatch, useSelector } from 'react-redux'

import { reset } from '../store'

// Remove the invoice ID from the Session Storage.
export const removeInvoiceId = (): void => {
  const dispatch = useDispatch()
  dispatch(reset())
}

/**
 * Appends query parameters to a given path and returns the updated path with the query string.
 *
 * @param {string} path - The base path to which the query parameters should be added.
 * @param {object} params - An object containing key-value pairs to be added as query parameters.
 * @return {string} The updated path with the appended query parameters.
 */
function addQueryParams(path: string, params: object) {
  // Get the location and full route.
  const location = window.location.origin
  const fullRoute = location + path

  // Create a URL object and search params.
  const urlObj = new URL(fullRoute)
  const searchParams = new URLSearchParams(urlObj.search)

  // Add the params to the search params.
  for (const [key, value] of Object.entries(params)) {
    searchParams.append(key, value)
  }

  // Set the search params to the URL object.
  urlObj.search = searchParams.toString()

  let urlString = urlObj.toString()

  // Remove fullRoute from urlString
  urlString = urlString.replace(window.location.origin, '')

  return urlString
}

/**
 * Escapes special characters in a given string to prevent unintended behavior or security vulnerabilities.
 *
 * @param {string} string - The input string to be sanitized.
 * @return {string} A sanitized string with special characters replaced by their corresponding HTML entities.
 */
function sanitize(string: string) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  }
  const reg = /[&<>"'/]/gi
  return string.replace(reg, (match): string => map[match as keyof typeof map])
}

type InvoiceStateAction = {
  invoice: {
    isInvoice: boolean
    clientId: string
    documentId: string
  }
}
/**
 * Generates a URL with query parameters and an optional token based on
 * the provided route and the state of the invoice.
 *
 * If the invoice state indicates an active invoice (`isInvoice` is true),
 * query parameters for `doc_id` (document ID) and `id` (client ID) are added
 * to the given route. If a token is provided, it is appended to the URL as
 * an additional parameter.
 *
 * @param {string} route - The base URL or route to be modified.
 * @param {string} [token=''] - An optional token to append to the URL.
 * @returns {string} - The modified URL with the required query parameters and optional token.
 */
export const generateRouteWithInvoice = (route: string, token = ''): string => {
  const { isInvoice, clientId, documentId } = useSelector((state: InvoiceStateAction) => state.invoice)
  let sanitizedUrl = route

  if (isInvoice && clientId && documentId) {
    // Sanitize parameter keys and values
    const params = {
      ['doc_id']: sanitize(documentId),
      ['id']: sanitize(clientId),
    }
    sanitizedUrl = addQueryParams(route, params)
  }

  // Add token if it exists and sanitize it
  return token === '' ? sanitizedUrl : `${sanitizedUrl}&${sanitize(token)}`
}
